import * as Sentry from "@sentry/browser";

// Initialize Sentry for error monitoring
// DSN is a publishable key (safe to include in client-side code)
export const initSentry = () => {
  // Only initialize in production
  if (import.meta.env.PROD) {
    Sentry.init({
      dsn: "https://87bd0d04707eca620e10910719cc4ab6@o4510707482165248.ingest.us.sentry.io/4510707486425088",
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      ],
      // Performance Monitoring
      tracesSampleRate: 0.1, // 10% of transactions for performance monitoring
      // Session Replay
      replaysSessionSampleRate: 0.1, // 10% of sessions
      replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
      // Environment
      environment: import.meta.env.MODE,
      // Release tracking (uses build timestamp)
      release: `vle-plus@${new Date().toISOString().split('T')[0]}`,
      // Filter out noisy errors
      ignoreErrors: [
        // Browser extensions
        /chrome-extension/,
        /moz-extension/,
        // Network errors that are expected
        "Failed to fetch",
        "NetworkError",
        "Load failed",
        // User-initiated navigation
        "AbortError",
      ],
      // Before sending, add extra context
      beforeSend(event) {
        // Don't send errors in development
        if (import.meta.env.DEV) {
          console.error("Sentry would send:", event);
          return null;
        }
        return event;
      },
    });

    console.log("[Sentry] Error monitoring initialized");
  } else {
    console.log("[Sentry] Skipped in development mode");
  }
};

// Helper to capture exceptions with context
export const captureError = (error: Error, context?: Record<string, unknown>) => {
  console.error("[Error]", error, context);
  
  if (import.meta.env.PROD) {
    Sentry.captureException(error, {
      extra: context,
    });
  }
};

// Helper to add user context
export const setUserContext = (userId: string, email?: string, tenantId?: string) => {
  Sentry.setUser({
    id: userId,
    email,
    tenant_id: tenantId,
  });
};

// Helper to clear user context (on logout)
export const clearUserContext = () => {
  Sentry.setUser(null);
};

// Helper to add breadcrumb for debugging
export const addBreadcrumb = (message: string, category: string, data?: Record<string, unknown>) => {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: "info",
  });
};
